import React from 'react';
import trendData from '../components/trendData.json';
import { FaInstagram, FaTwitter, FaTiktok, FaYoutube, FaSnapchatGhost } from "react-icons/fa";

const TrendAnalyzerMockUpdated = () => {
  // Mock data for influencers
  const influencers = [
    {
      name: "Marques Brownlee",
      platform: "YouTube",
      handle: "@MKBHD",
      link: "https://www.youtube.com/@mkbhd",
      icon: FaYoutube,
      color: "from-red-500 to-red-600",
      viralContent: [
        { hook: "Unboxing the New iPhone!", description: "Full review of the latest iPhone, including camera tests and performance benchmarks." },
        { hook: "Is AI Going to Replace Us?", description: "Discussing the implications and ethical considerations of advanced AI technologies." },
      ],
    },
    {
      name: "Unbox Therapy",
      platform: "YouTube",
      handle: "@UnboxTherapy",
      link: "https://www.youtube.com/@unboxtherapy",
      icon: FaYoutube,
      color: "from-red-500 to-red-600",
      viralContent: [
        { hook: "The Most Expensive Laptop Ever!", description: "Hands-on with the latest, most luxurious laptop, testing its build quality and features." },
        { hook: "Crazy Tech Gadgets!", description: "Exploring the most innovative and unusual tech gadgets from around the world." },
      ],
    },
    {
      name: "TechCrunch",
      platform: "Twitter",
      handle: "@TechCrunch",
      link: "https://twitter.com/TechCrunch",
      icon: FaTwitter,
      color: "from-blue-400 to-blue-500",
      viralContent: [
        { hook: "Breaking: New AI Chip Announced!", description: "Exclusive details on a new AI chip set to revolutionize the tech industry." },
        { hook: "The Future of VR!", description: "Analyzing the next steps for virtual reality and its potential applications." },
      ],
    },
    {
      name: "Tech Insider",
      platform: "Instagram",
      handle: "@techinsider",
      link: "https://www.instagram.com/techinsider/",
      icon: FaInstagram,
      color: "from-pink-500 to-purple-500",
      viralContent: [
        { hook: "This Robot Can Fold Your Laundry!", description: "Demonstrating a futuristic robot that makes everyday tasks easier." },
        { hook: "Smart Home Tech!", description: "Showcasing the latest gadgets that can transform your home into a smart home." },
      ],
    },
    {
      name: "Mrwhosetheboss",
      platform: "YouTube",
      handle: "@Mrwhosetheboss",
      link: "https://www.youtube.com/@Mrwhosetheboss",
      icon: FaYoutube,
      color: "from-red-500 to-red-600",
      viralContent: [
        { hook: "iPhone vs. Android: Which is Better?", description: "A comprehensive comparison of the latest iPhone and Android devices." },
        { hook: "The Future of Gaming!", description: "Exploring the latest innovations in gaming technology, from VR to cloud gaming." },
      ],
    },
    {
      name: "Snapchat Tech",
      platform: "Snapchat",
      handle: "@snaptech",
      link: "https://www.snapchat.com/add/snaptech", // Example link
      icon: FaSnapchatGhost,
      color: "from-yellow-400 to-yellow-500",
      viralContent: [
        { hook: "Check Out This New Snapchat Lens!", description: "Showcasing an innovative new augmented reality lens filter." },
        { hook: "Snapchat Tech Updates", description: "The latest updates and features on Snapchat." },
      ],
    },
    {
      name: "TikTok Tech Tips",
      platform: "TikTok",
      handle: "@tiktoktechtips",
      link: "https://www.tiktok.com/@tiktoktechtips", // Example link
      icon: FaTiktok,
      color: "from-black to-gray-800",
      viralContent: [
        { hook: "Hidden TikTok Features!", description: "Uncovering secret tips and tricks for using TikTok." },
        { hook: "The Best TikTok Tech Accounts", description: "A review of the best tech-related accounts on TikTok." },
      ],
    },
  ];

  // Mock data for trending topics
  const trendingTopics = [
    "AI in Daily Life",
    "Next-Gen Gaming",
    "SpaceX Innovations",
    "Renewable Energy Tech",
    "Cybersecurity Threats",
  ];

  // Mock data for trending audio/voices
  const trendingAudios = [
    { title: "Robotic Voice Trend", description: "This robotic voice is going viral on TikTok and Instagram Reels.", link: "#" },
    { title: "AI-Generated Music", description: "An AI-generated track that's become a sensation in tech circles.", link: "#" },
    { title: "Fast-Paced Upbeat Beats", description: "Used in quick tech reviews and unboxing.", link: "#" },
    { title: "Motivational Tech Speech", description: "Inspirational speech about technology impact.", link: "#" }
  ];
  // Mock data for trending hashtags
  const trendingHashtags = [
    "#TechTrends",
    "#AIRevolution",
    "#FutureTech",
    "#GadgetGoals",
    "#SmartHome",
    "#TechInnovations",
    "#CodingLife",
    "#Cybersecurity",
    "#VRGaming",
    "#TechReviews",
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Hero Section */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-white">Viral Trend Analysis</h1>
        <p className="text-gray-400 mt-2">
          AI analyzes millions of viral videos from YouTube Shorts, Instagram Reels, and TikTok to identify winning formats, hooks, and styles in your niche.
        </p>
      </div>

      {/* Trend Data Section */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-4">
          Trend Analysis
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trendData.map((trend, index) => (
            <div key={index} className="bg-gray-700/50 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                {trend.title}
              </h3>
              <ul className="text-gray-300 space-y-1">
                {trend.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Influencer Section */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-4">
          Top Influencers in Technology
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {influencers.map((influencer, index) => (
            <div key={index} className="bg-gray-700/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-white">
                <influencer.icon className={`w-5 h-5 bg-gradient-to-r rounded-full p-1 ${influencer.color}`} />
                <a href={influencer.link} target="_blank" rel="noopener noreferrer">
                  <h3 className="font-semibold hover:underline">{influencer.name}</h3>
                </a>
                <span className="text-gray-400">{influencer.handle}</span>
              </div>
              <ul className="mt-2 space-y-2">
                {influencer.viralContent.map((content, idx) => (
                  <li key={idx} className="text-gray-300">
                    <p className="font-medium text-blue-400">{content.hook}</p>
                    <p className="text-sm">{content.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Topics Section */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-4">
          Trending Topics in Technology
        </h2>
        <div className="flex flex-wrap gap-4">
          {trendingTopics.map((topic, index) => (
            <span key={index} className="bg-gray-700/50 text-white px-4 py-2 rounded-full text-sm">
              {topic}
            </span>
          ))}
        </div>
      </div>

      {/* Viral Audio/Voice Section */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-4">
          Trending Viral Audios/Voices
        </h2>
        <div className="space-y-4">
          {trendingAudios.map((audio, index) => (
            <div key={index} className="bg-gray-700/50 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-white">{audio.title}</h3>
              <p className="text-gray-300 text-sm">{audio.description}</p>
              {/* Add an audio player or link to the audio here */}
              <a href={audio.link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline mt-2 inline-block">
                 Listen Now
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Hashtags Section */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-4">
          Trending Hashtags
        </h2>
        <div className="flex flex-wrap gap-4">
          {trendingHashtags.map((hashtag, index) => (
            <span key={index} className="bg-gray-700/50 text-white px-4 py-2 rounded-full text-sm">
              {hashtag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrendAnalyzerMockUpdated;
